import React, { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from "react"
import { Button, Flex, Heading, Image as ChakraImage, Text } from "@chakra-ui/react"

import { apiSaveFaceScanUrl, faceScanAnalysis, sendFileToImageStorage } from "~/api"
import type { ColorString, QuizId, URLString } from "~/types"
import { BasePageWrapper } from "~/components/shared/BasePageWrapper/BasePageWrapper"
import {
  NextButton,
  NextButtonBottomSlideUpContainer as NextButtonContainer,
} from "~/components/shared/NextButton"
import type { NextPageCb } from "~/hooks/useNextQuiz"
import { useQuizHistory, useUserId } from "~/hooks/useQuizHistory"
import { TT, TTString } from "~/components/shared/AttributedString"
import { useAmplitude } from "~/hooks/analytics/useAmplitude"
import { createStopwatch } from "~/utils"

import { FaceScanLoading } from "./FaceScanLoading"
import { base64ToUrl, fileToBase64 } from "./fileToBase64"
import defaultImage from "./assets/selfie-photo.webp?url"
import { useScanStore } from "./useScanStore"
import { FaceScannerPushModal } from "./FaceScannerPushModal"
import { ResponseStatus } from "~/generated/networking"
import { ServerApiError } from "~/errors"

const IS_PUSH_MODAL_SCENARIO_ACTIVE = true

const FileInput: FC<{ label: string; onChange: (f: File) => void }> = ({
  label,
  onChange,
  ...props
}) => {
  const ref = useRef<HTMLInputElement>(null)
  const { logFaceScanStatus } = useAmplitude()
  const onClick = () => {
    if (ref.current) {
      logFaceScanStatus("startSelectPhoto")
      ref.current.click()
    }
  }
  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        onChange(file)
      }
    },
    [onChange]
  )
  return (
    <>
      <Button variant="next" w="100%" minH="56px" onClick={onClick} {...props}>
        {label}
      </Button>
      <input
        type="file"
        name="picture"
        accept="image/*"
        capture="user"
        style={{ display: "none" }}
        ref={ref}
        onChange={onChangeHandler}
      />
    </>
  )
}

type Variant = {
  text: string
}

const defaultTitle = "Let’s analyze your skin"
const defaultDescription =
  "Take a selfie to get your skin analysis. Make sure your face is well-lit and all the makeup is removed."

const SCAN_STORAGE_OPTS = {
  maxDimension: window.screen.availHeight * window.screen.pixelDepth,
  instant: true,
} as const

const preloadImage = (src: string) => {
  /* TODO create unified system for preload external resources */
  const img = new Image()
  img.src = src
}

const preloadImages = (...images: string[]) => {
  return new Promise((resolve, _reject) => {
    images.forEach(preloadImage)
    resolve(undefined)
  })
}

const loadImage = async (url: string) => {
  return new Promise<HTMLImageElement>((resolve) => {
    const i = new Image()
    i.onload = () => resolve(i)
    i.src = url
  })
}

const applyMask = async (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  maskColor: string,
  maskB64: Base64String
) => {
  const maskImg = await loadImage(base64ToUrl(maskB64))

  ctx.drawImage(maskImg, 0, 0, width, height)

  ctx.globalCompositeOperation = "multiply"
  ctx.fillStyle = maskColor
  ctx.fillRect(0, 0, width, height)
  ctx.globalCompositeOperation = "screen"
}

/* TODO use ImageBitmap instead Base64String here */
/* TODO resize image */
const makeCanvas = async (photo: Base64String, masks: [ColorString, Base64String][]) => {
  const photoUrl = base64ToUrl(photo)
  const photoImg = await loadImage(photoUrl)
  const masksCanvas = document.createElement("canvas")

  const height = photoImg.height
  const width = photoImg.width

  const photoCanvas = document.createElement("canvas")
  photoCanvas.width = width
  photoCanvas.height = height

  const ctx = photoCanvas.getContext("2d")
  if (!ctx) return

  ctx.drawImage(photoImg, 0, 0)
  ctx.fillStyle = "#00000066"
  ctx.fillRect(0, 0, width, height)

  const ctx2 = masksCanvas.getContext("2d")
  masksCanvas.width = width
  masksCanvas.height = height

  if (!ctx2) return

  for await (const i of masks) {
    const [maskColor, maskB64] = i
    await applyMask(ctx2, width, height, maskColor, maskB64)
  }

  ctx2.drawImage(photoCanvas, 0, 0, width, height)

  return masksCanvas
}

const toBlobAsync = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob)
    })
  })

export const FaceScannerInitPage: FC<{
  title?: TTString
  description?: TTString
  image?: string
  variants?: Variant[]
  next: NextPageCb
}> = ({
  title = defaultTitle,
  description = defaultDescription,
  image = defaultImage,
  variants = [],
  next,
}) => {
  const scanQuestionResponse = variants[0]?.text ?? "Take a selfie"
  const skipScanQuestionResponse = variants[1]?.text ?? "Do it later"
  const { userId } = useUserId()
  const [scan, setScan] = useState<URLString>("" as URLString)
  const [status, setStatus] = useState<"init" | "loading" | "done">("init")

  const { put: putScanStore, clear: clearScanStore } = useScanStore()
  const { logFaceScanStatus, logFaceScanError, logFaceScanAnalysisTime } = useAmplitude()
  const {
    params: { quiz },
  } = useQuizHistory()

  const [isShowPushModal, setIsShowPushModal] = useState(false)

  useEffect(() => {
    logFaceScanStatus(status)
  }, [status])

  const onChange = useCallback(
    async (file: File) => {
      setStatus("loading")
      if (file && userId && quiz) {
        const timeFromStart = createStopwatch()
        try {
          const encoded = await fileToBase64(file)
          if (encoded) {
            const encodedScan = base64ToUrl(encoded)
            setScan(encodedScan)
            const encodeTime = timeFromStart()
            const result = await faceScanAnalysis(userId, encoded)
            const analysisTime = timeFromStart()

            const { scanId, masks } = result
            const canvas = await makeCanvas(encoded, Object.values(masks))
            let scanUrl = undefined
            if (canvas) {
              const blob = await toBlobAsync(canvas)
              if (blob) {
                scanUrl = (await sendFileToImageStorage(blob, SCAN_STORAGE_OPTS)) as URLString
              }
            }

            if (scanUrl) {
              const resp = await apiSaveFaceScanUrl(userId, quiz, scanUrl, scanId)
              if (resp.response?.status !== ResponseStatus.SUCCESS) {
                throw new ServerApiError(`Server ResponseStatus is ${resp.response?.status}`)
              }
              putScanStore({ id: scanId, image: scanUrl })
              preloadImages(scanUrl)
            }
            const putResponseToStoreTime = timeFromStart()
            const times = {
              encode: encodeTime,
              analysis: analysisTime - encodeTime,
              putToStore: putResponseToStoreTime - analysisTime,
            }

            logFaceScanAnalysisTime(times)
            setStatus("done")
            next([scanQuestionResponse])
          }
        } catch (error) {
          const analysisTime = timeFromStart()
          const times = { analysis: analysisTime }
          logFaceScanAnalysisTime(times)

          clearScanStore()
          logFaceScanError()
          const result = window.confirm("Oops... It doesn't look like a selfie. Try Again?")
          if (result) {
            setStatus("init")
          } else {
            next([skipScanQuestionResponse])
          }
        }
      }
    },
    [next, userId, putScanStore, clearScanStore]
  )

  const onSkip = useCallback(() => {
    clearScanStore()
    next([skipScanQuestionResponse])
  }, [next, clearScanStore])

  const showPushModal = () => {
    setIsShowPushModal(true)
  }

  const goToScanFooter = (
    <Flex direction="column" gap={2}>
      <FileInput label={scanQuestionResponse} onChange={onChange} />
      <NextButton variant="nextWhite" label={skipScanQuestionResponse} onClick={onSkip} />
    </Flex>
  )
  const goToModalFooter = (
    <Flex direction="column" gap={2}>
      <FileInput label={scanQuestionResponse} onChange={onChange} />
      <NextButton variant="nextWhite" label={skipScanQuestionResponse} onClick={showPushModal} />
    </Flex>
  )
  const goToScanModalFooter = (
    <Flex direction="column" gap={2}>
      <FileInput label="Let’s take a selfie" onChange={onChange} />
      <NextButton
        bgColor="Base/neutralSecondary"
        variant="nextWhite"
        label="Continue without a selfie"
        onClick={onSkip}
      />
    </Flex>
  )

  if (status === "loading" || status === "done") {
    return <FaceScanLoading scan={scan} />
  }

  return (
    <BasePageWrapper>
      <ChakraImage
        w="full"
        minH="245px"
        alt=""
        src={image || defaultImage}
        borderRadius="2xl"
        sx={{ aspectRatio: "327/245" }}
      />
      <Flex direction="column" gap={3} alignItems="center" mt={6}>
        <Heading size="Header/Primary" textAlign="center">
          <TT>{title}</TT>
        </Heading>
        <Text textStyle="Paragraph/Primary" textAlign="center">
          <TT>{description}</TT>
        </Text>
      </Flex>
      <NextButtonContainer visible>
        <FaceScannerPushModal
          footer={goToScanModalFooter}
          isOpen={isShowPushModal}
          onClose={() => setIsShowPushModal(false)}
        />
        {IS_PUSH_MODAL_SCENARIO_ACTIVE ? goToModalFooter : goToScanFooter}
      </NextButtonContainer>
    </BasePageWrapper>
  )
}
